import { Tick02Icon, CheckUnread03Icon, Settings05Icon } from '@hugeicons-pro/core-stroke-rounded';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { router } from 'expo-router';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Platform, TouchableOpacity, View } from 'react-native';

import SafetySettingsContent from '@/components/domain/common/SafetySettingsContent';
import { Badge, BadgeText } from '@/components/gluestack/badge';
import { Menu, MenuItem, MenuItemLabel, MenuSeparator } from '@/components/gluestack/menu';
import { ContextSheet } from '@/components/ui/ContextSheet';
import { CountBadge } from '@/components/ui/CountAndMyTypeBadge';
import { MenuItemIcon } from '@/components/ui/MenuItemIcon';
import { useAccountSettings } from '@/hooks/useAccountSettings';
import { useIsLargeScreen } from '@/hooks/useIsLargeScreen';
import { invalidateFilteredListQueries } from '@/hooks/useQueryCacheUtils';
import { useToastMessages } from '@/hooks/useToastMessages';
import { AccountService } from '~/services/domain/AccountService';

interface IMyTypeFilterMenuProps {
  count?: number;
  isMyTypeFiltered?: boolean | null;
  size?: 'xs' | 'sm' | 'md';
}

export function MyTypeFilterMenu({
  count = 0,
  isMyTypeFiltered,
  size = 'md',
}: IMyTypeFilterMenuProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { showSuccessToast, showErrorToast } = useToastMessages();

  const isLargeScreen = useIsLargeScreen();
  const [isSafetySheetOpen, setIsSafetySheetOpen] = useState(false);
  const [settingsQueryEnabled, setSettingsQueryEnabled] = useState(false);

  const isEnabled = isMyTypeFiltered === true;
  const needsAccountSettings = Platform.OS !== 'ios';

  const { data: accountSettings } = useAccountSettings({
    enabled: needsAccountSettings && settingsQueryEnabled,
  });

  const handleMenuOpen = useCallback(() => {
    if (!needsAccountSettings) return;
    setSettingsQueryEnabled(true);
  }, [needsAccountSettings]);

  const isSensitiveVisible = accountSettings?.SafeModeOff === true;

  const enableMutation = useMutation({
    mutationFn: AccountService.enableMyTypeFilter,
    onSuccess: () => {
      invalidateFilteredListQueries(queryClient);
      showSuccessToast(t('myTypeFilter.enabled'));
    },
    onError: (error) => {
      showErrorToast(t('myTypeFilter.enableError'), error);
    },
  });

  const disableMutation = useMutation({
    mutationFn: AccountService.disableMyTypeFilter,
    onSuccess: () => {
      invalidateFilteredListQueries(queryClient);
      showSuccessToast(t('myTypeFilter.disabled'));
    },
    onError: (error) => {
      showErrorToast(t('myTypeFilter.disableError'), error);
    },
  });

  const handleToggle = useCallback(() => {
    if (isEnabled) {
      disableMutation.mutate();
    } else {
      enableMutation.mutate();
    }
  }, [isEnabled, enableMutation, disableMutation]);

  const handleConfigure = useCallback(() => {
    router.push('/account/my-type-filter');
  }, []);

  const handleConfigureSensitive = useCallback(() => {
    setIsSafetySheetOpen(true);
  }, []);

  if (isMyTypeFiltered === null || isMyTypeFiltered === undefined) {
    return <CountBadge count={count} size={size} />;
  }

  return (
    <>
      <Menu
        placement={isLargeScreen ? 'bottom left' : 'bottom'}
        offset={8}
        onOpen={handleMenuOpen}
        trigger={(props: any) => (
          <TouchableOpacity activeOpacity={0.7} {...props} className="select-none">
            <CountBadge count={count} isMyTypeFiltered={isMyTypeFiltered} size={size} />
          </TouchableOpacity>
        )}>
        <MenuItem
          key="title"
          textValue={t('myTypeFilter.title')}
          disabled
          className="bg-transparent data-[active=true]:bg-transparent data-[focus=true]:bg-transparent data-[hover=true]:bg-transparent">
          <View className="flex-row items-center gap-4">
            <MenuItemLabel className="text-sm font-bold uppercase text-typography-500">
              {t('myTypeFilter.title')}
            </MenuItemLabel>
            <Badge
              size="md"
              className={`rounded-full ${isEnabled ? 'bg-success-500' : 'bg-background-300'}`}>
              <BadgeText
                className={`font-bold ${isEnabled ? 'text-white' : 'text-typography-600'}`}>
                {isEnabled ? t('myTypeFilter.statusEnabled') : t('myTypeFilter.statusDisabled')}
              </BadgeText>
            </Badge>
          </View>
        </MenuItem>

        <MenuItem
          key="toggle"
          textValue={isEnabled ? t('myTypeFilter.disableOption') : t('myTypeFilter.enableOption')}
          onPress={handleToggle}>
          <MenuItemIcon icon={isEnabled ? CheckUnread03Icon : Tick02Icon} />
          <MenuItemLabel>
            {isEnabled ? t('myTypeFilter.disableOption') : t('myTypeFilter.enableOption')}
          </MenuItemLabel>
        </MenuItem>

        <MenuItem key="configure" textValue={t('myTypeFilter.configure')} onPress={handleConfigure}>
          <MenuItemIcon icon={Settings05Icon} />
          <MenuItemLabel>{t('myTypeFilter.configure')}</MenuItemLabel>
        </MenuItem>

        {Platform.OS !== 'ios' && (
          <>
            <MenuSeparator className="my-2" />

            <MenuItem
              key="sensitive-title"
              textValue={t('sensitiveContent.title')}
              disabled
              className="bg-transparent data-[active=true]:bg-transparent data-[focus=true]:bg-transparent data-[hover=true]:bg-transparent">
              <View className="flex-row items-center gap-4">
                <MenuItemLabel className="text-sm font-bold uppercase text-typography-500">
                  {t('sensitiveContent.title')}
                </MenuItemLabel>
                {accountSettings && (
                  <Badge
                    size="md"
                    className={`rounded-full ${isSensitiveVisible ? 'bg-success-500' : 'bg-error-500'}`}>
                    <BadgeText className="font-bold text-white">
                      {isSensitiveVisible
                        ? t('sensitiveContent.statusVisible')
                        : t('sensitiveContent.statusHidden')}
                    </BadgeText>
                  </Badge>
                )}
              </View>
            </MenuItem>

            <MenuItem
              key="configure-sensitive"
              textValue={t('sensitiveContent.configure')}
              onPress={handleConfigureSensitive}>
              <MenuItemIcon icon={Settings05Icon} />
              <MenuItemLabel>{t('sensitiveContent.configure')}</MenuItemLabel>
            </MenuItem>
          </>
        )}
      </Menu> 

      {Platform.OS !== 'ios' && (
        <ContextSheet
          isOpen={isSafetySheetOpen}
          onClose={() => setIsSafetySheetOpen(false)}
          contentComponent={SafetySettingsContent}
          title={t('settings.safety.title')}
        />
      )}
    </>
  );
}
